import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Log API URL on startup
console.log('🌐 Frontend connecting to:', API_URL);
console.log('📦 Environment:', process.env.NODE_ENV);

// CSRF token management
let csrfToken = null;
let csrfTokenExpiry = null;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 second timeout
});

// Function to fetch CSRF token
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/fees/csrf-token`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      withCredentials: true
    });
    csrfToken = response.data.csrfToken;
    csrfTokenExpiry = Date.now() + (50 * 60 * 1000); // Expires in 50 minutes (10 min before server expiry)
    console.log('🔐 CSRF token fetched');
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Function to get valid CSRF token
const getCsrfToken = async () => {
  // Return cached token if still valid
  if (csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
    return csrfToken;
  }
  // Fetch new token if expired or missing
  return await fetchCsrfToken();
};

// Add token and CSRF token to requests
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    console.log('📤 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: config.baseURL + config.url,
      hasToken: !!token
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing operations
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrf = await getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
        console.log('🔐 CSRF token added to request');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Log all connection errors to help debug
    if (!error.response) {
      console.error('❌ Network Error - Cannot connect to backend:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        message: error.message,
        code: error.code
      });
    } else {
      // Only log detailed errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ API Error:', {
          url: error.config?.url,
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
          data: error.response?.data
        });
      }
    }
    
    // Handle CSRF token errors - retry once with new token
    if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
      console.log('🔄 CSRF token invalid, fetching new token and retrying...');
      const newToken = await fetchCsrfToken();
      if (newToken && error.config) {
        error.config.headers['X-CSRF-Token'] = newToken;
        return api.request(error.config);
      }
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export default api;
